import React from 'react';

import { Button, ButtonProps, SIZES } from './Button';
import classnames from 'classnames';

export interface RoundedButtonGroupItem {
  value: string | number;
  label: React.ReactNode;
  onClick: () => void;
  isSelected?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  disabled?: boolean;
  buttonProps?: Partial<Omit<ButtonProps, 'onClick' | 'label' | 'children' | 'isSelected'>>;
}

export interface RoundedButtonGroupProps {
  items: RoundedButtonGroupItem[];
  className?: string;
  buttonType?: ButtonProps['type'];
  buttonSize?: ButtonProps['size'];
  selectedClassName?: string;
  unselectedClassName?: string;
}

const STATE_CLASSES = {
  tertiary: {
    selected:
      '!border !border-pink-800 !bg-pink-800 !text-gray-200 hover:!border-pink-700 hover:!bg-pink-700 hover:!text-gray-200 active:!border-pink-500 active:!bg-pink-500 active:!text-white focus:!border-pink-500',
    unselected:
      '!border hover:!border-pink-900 hover:!bg-pink-900 hover:!text-gray-200 active:!border-pink-600 active:!bg-pink-600 active:!text-white focus:!border-pink-600',
  },
  primary: {
    selected: 'active:!bg-brand-300 active:!border-brand-300 active:!text-brand-950',
    unselected: 'active:!bg-brand-300 active:!border-brand-300 active:!text-brand-950',
  },
  secondary: {
    selected: 'active:!bg-pink-500 active:!border-pink-500 active:!text-white',
    unselected: 'active:!bg-pink-500 active:!border-pink-500 active:!text-white',
  },
  default: {
    selected: 'active:!opacity-75',
    unselected: 'active:!opacity-75',
  },
} as const;

export const RoundedButtonGroup: React.FC<RoundedButtonGroupProps> = ({
  items,
  className,
  buttonType = 'tertiary',
  buttonSize = SIZES.small,
  selectedClassName,
  unselectedClassName,
}) => {
  const getStateClasses = (type: ButtonProps['type'], isSelected: boolean) => {
    if (isSelected && selectedClassName) return selectedClassName;
    if (!isSelected && unselectedClassName) return unselectedClassName;

    const buttonTypeKey = type === 'tertiary' || !type ? 'tertiary' : type || 'default';
    const stateKey = isSelected ? 'selected' : 'unselected';
    const typeConfig =
      STATE_CLASSES[buttonTypeKey as keyof typeof STATE_CLASSES] || STATE_CLASSES.default;

    return typeConfig[stateKey];
  };

  return (
    <div className={classnames('flex', className)}>
      {items.map((item, index) => {
        const isFirst = index === 0;
        const isLast = index === items.length - 1;
        const isSelected = item.isSelected ?? false;
        const itemButtonType = item.buttonProps?.type || buttonType;

        const roundedClasses = classnames('!rounded-none', {
          '!rounded-l-[29px]': isFirst,
          '!rounded-r-[29px]': isLast,
        });

        const borderClasses = {
          '-mr-px': !isLast,
        };

        const stateClasses = getStateClasses(itemButtonType, isSelected);
        const iconPosition = item.iconPosition || 'left';
        const hasIcon = !!item.icon;

        return (
          <Button
            type={itemButtonType}
            size={item.buttonProps?.size || buttonSize}
            key={item.value}
            label={String(item.value)}
            isSelected={isSelected}
            disabled={item.disabled}
            isIconRight={hasIcon && iconPosition === 'right'}
            className={classnames(
              roundedClasses,
              borderClasses,
              stateClasses,
              item.buttonProps?.className,
            )}
            onClick={item.onClick}
            {...item.buttonProps}
          >
            {hasIcon && iconPosition === 'left' && <span className="mr-1">{item.icon}</span>}
            {item.label}
            {hasIcon && iconPosition === 'right' && <span className="ml-1">{item.icon}</span>}
          </Button>
        );
      })}
    </div>
  );
};
